import React, { useMemo } from 'react';
import hasDom from 'has-dom';
import UAparser from 'ua-parser-js';
import { AnalyticsEngine } from 'reaxl-analytics';
import { Provider as ReduxProvider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { OptimizelyPageProvider } from '@atc/optimizely-toolkit';
import { Provider as FeatureFlagsProvider } from '@atc/features';
import { batchDispatchMiddleware } from 'redux-batched-actions';
import { DeviceProvider } from '@atc/react-device';

import useAtcui from './useAtcui';

let store;

function getStore(config) {
    if (!store) {
        store = configureStore({
            reducer: config.reducers.reduce(
                (acc, duck) => ({
                    ...acc,
                    [duck.store]: duck.reducer,
                }),
                {}
            ),
            middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(batchDispatchMiddleware),
            preloadedState: config.initialState,
        });
    }
    return store;
}

export default function AtcuiProviders({
    analytics = {},
    children,
    reducers = [],
    stylesheets = [],
    flags = {},
    iconFontPath = '.',
    containerRef,
    initialState = {},
    ...rest
}) {
    const memoizedStore = useMemo(() => getStore({ reducers, initialState }), [reducers, initialState]);

    useAtcui({ iconFontPath });

    const { browser } = hasDom() ? UAparser(window.navigator.userAgent) : {};

    const deviceProps = {
        browser: browser?.name,
        version: browser?.version,
    };

    return (
        <ReduxProvider store={memoizedStore}>
            <DeviceProvider {...deviceProps}>
                <FeatureFlagsProvider flags={flags}>
                    <OptimizelyPageProvider disabled>
                        {children}
                        {stylesheets.length > 0 &&
                            stylesheets.map((href, index) => (
                                <link
                                    rel="stylesheet"
                                    href={href}
                                    key={`wallet-style-${index}`}
                                />
                            ))}
                        <AnalyticsEngine {...analytics} />
                    </OptimizelyPageProvider>
                </FeatureFlagsProvider>
            </DeviceProvider>
        </ReduxProvider>
    );
}
